'use client';

import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@cc-shopping/platform-analytics/hooks/useAnalytics';
import { useEoru } from '@cc-shopping/platform-analytics/hooks/useEoru';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { useIdentityContext } from '@cc-shopping/platform-analytics/IdentityContext';
import { datadogRum } from '@datadog/browser-rum';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { type SubmitEvent, useRef, useState } from 'react';
import * as v from 'valibot';
import { useActivityId } from '../Analytics/ActivityIdProvider';
import { ACTION_ERROR, ACTION_SUCCESS, DRIVER_LOCATION_LABEL, ENTITY_SECTION } from '../constants';
import { AlertIcon } from '../Images/AlertIcon';
import { Spinner } from './Spinner';
import { emailSchema } from './schema';
type FormProps = {
  onError: () => void;
  onSuccess: () => void;
  isTravelNewsLetter?: boolean;
};
export const Form = t0 => {
  const $ = _c(63);
  const {
    onError,
    onSuccess,
    isTravelNewsLetter
  } = t0;
  const [inputError, setInputError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emailInputRef = useRef(null);
  const {
    createEORU,
    getCsrfToken,
    refreshCsrfToken
  } = useEoru();
  const pathname = usePathname() || "";
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname.split("/").pop() || "";
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const slug = t1;
  const activityId = useActivityId();
  const {
    isLoggedIn,
    sheaId,
    identityId,
    email: currentlyLoggedInEmail
  } = useIdentityContext();
  const {
    analytics: segmentAnalytics,
    deployableName,
    deployableVersion,
    deviceId,
    gitSha
  } = useAnalytics();
  let t2;
  if ($[2] !== activityId) {
    t2 = {
      actionName: "",
      actionSection: ENTITY_SECTION,
      actionType: "select",
      parentActivityId: activityId
    };
    $[2] = activityId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fireSubmitPageAction = usePageAction(t2);
  let t3;
  if ($[4] !== fireSubmitPageAction || $[5] !== onSuccess) {
    t3 = () => {
      fireSubmitPageAction({
        action_name: ACTION_SUCCESS
      });
      onSuccess();
    };
    $[4] = fireSubmitPageAction;
    $[5] = onSuccess;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSuccess = t3;
  let t4;
  if ($[7] !== fireSubmitPageAction || $[8] !== onError) {
    t4 = () => {
      fireSubmitPageAction({
        action_name: ACTION_ERROR
      });
      onError();
    };
    $[7] = fireSubmitPageAction;
    $[8] = onError;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleError = t4;
  const buttonText = isTravelNewsLetter ? "SUBSCRIBE FOR FREE" : "SIGN UP NOW";
  let t5;
  if ($[10] !== createEORU || $[11] !== currentlyLoggedInEmail || $[12] !== deployableName || $[13] !== deployableVersion || $[14] !== deviceId || $[15] !== getCsrfToken || $[16] !== gitSha || $[17] !== handleError || $[18] !== handleSuccess || $[19] !== identityId || $[20] !== isLoggedIn || $[21] !== refreshCsrfToken || $[22] !== segmentAnalytics || $[23] !== sheaId || $[24] !== slug) {
    t5 = async e => {
      e.preventDefault();
      const enteredValue = emailInputRef.current?.value;
      const validatedValue = v.safeParse(emailSchema, enteredValue);
      if (!validatedValue.success) {
        setInputError(true);
        return;
      }
      if (!segmentAnalytics) {
        datadogRum.addError(new Error("Segment instance not available."), {
          message: "No Segment instance to fire identify() analytics event."
        });
        handleError();
        return;
      }
      setSubmitting(true);
      let localCsrfToken = getCsrfToken();
      if (!localCsrfToken) {
        const retryTokenFetch = await refreshCsrfToken();
        if (!retryTokenFetch) {
          handleError();
          return;
        }
        localCsrfToken = retryTokenFetch;
      }
      const timestamp = new Date().toISOString();
      const isDifferentEmailFromCurrentlyLoggedInEmail = currentlyLoggedInEmail?.toLowerCase() !== validatedValue.output.toLowerCase();
      if (!isLoggedIn || isDifferentEmailFromCurrentlyLoggedInEmail) {
        const createdUserResponse = await createEORU({
          omitCredentials: isLoggedIn && isDifferentEmailFromCurrentlyLoggedInEmail,
          csrfToken: localCsrfToken,
          email: validatedValue.output,
          additionalSegmentAttributes: {
            eoru_registration_driver_page_name: `${slug.replace(/-/g, "_")}`,
            eoru_registration_driver_location: DRIVER_LOCATION_LABEL,
            eoru_registration: {
              registration_driver_label: DRIVER_LOCATION_LABEL,
              registration_ts: timestamp
            },
            latest_eoru_registered_at: timestamp,
            most_recent_traditional_device_id: deviceId ?? ""
          }
        });
        if (!createdUserResponse || !createdUserResponse.data) {
          handleError();
          return;
        }
      } else {
        if (!sheaId) {
          datadogRum.addError(new Error("shea_id not available."), {
            message: "User does not have a valid shea_id."
          });
          handleError();
          return;
        }
        const currentSegmentUser = await segmentAnalytics.segmentInstance?.user();
        currentSegmentUser?.traits({});
        segmentAnalytics.identify(sheaId, {
          deployable_name: deployableName,
          deployable_version: deployableVersion,
          eoru_registration_driver_page_name: `${slug.replace(/-/g, "_")}`,
          eoru_registration_driver_location: DRIVER_LOCATION_LABEL,
          eoru_registration: {
            registration_driver_label: DRIVER_LOCATION_LABEL,
            registration_ts: timestamp
          },
          latest_visitor_identity_id: identityId,
          member_identity_id: identityId,
          most_recent_traditional_device_id: deviceId ?? "",
          shea_id: sheaId
        }, {
          context: {
            app: {
              name: deployableName,
              version: gitSha
            }
          }
        });
      }
      setInputError(false);
      handleSuccess();
    };
    $[10] = createEORU;
    $[11] = currentlyLoggedInEmail;
    $[12] = deployableName;
    $[13] = deployableVersion;
    $[14] = deviceId;
    $[15] = getCsrfToken;
    $[16] = gitSha;
    $[17] = handleError;
    $[18] = handleSuccess;
    $[19] = identityId;
    $[20] = isLoggedIn;
    $[21] = refreshCsrfToken;
    $[22] = segmentAnalytics;
    $[23] = sheaId;
    $[24] = slug;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const handleSubmit = t5;
  let t6;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = clsx("flex", "flex-col", "gap-1");
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <label htmlFor="email" className={clsx("text-xsmall")}>Email address</label>;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== isTravelNewsLetter) {
    t8 = clsx("flex", "flex-col", {
      "lg:flex-row": isTravelNewsLetter
    }, {
      "md:flex-col": !isTravelNewsLetter
    }, "w-full", "gap-4");
    $[28] = isTravelNewsLetter;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = clsx("flex", "flex-col", "gap-1", "flex-1");
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const t10 = inputError ? "true" : "false";
  const t11 = inputError ? "email-error" : undefined;
  const t12 = !inputError;
  let t13;
  if ($[31] !== inputError || $[32] !== t12) {
    t13 = clsx("border", "box-border", "rounded", "w-full", "h-[44px]", "px-4", "outline-none", "focus:border-[#4b99e6]", "focus:shadow-[0_0_0_1px_#4b99e6]", {
      "border-neutral-dark": t12,
      "border-red-light": inputError,
      "bg-red-lightest": inputError
    });
    $[31] = inputError;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== inputError) {
    t14 = () => {
      if (inputError) {
        setInputError(false);
      }
    };
    $[34] = inputError;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== submitting || $[37] !== t10 || $[38] !== t11 || $[39] !== t13 || $[40] !== t14) {
    t15 = <input ref={emailInputRef} required={true} id="email" type="email" autoComplete="email" disabled={submitting} aria-invalid={t10} aria-describedby={t11} className={t13} onChange={t14} />;
    $[36] = submitting;
    $[37] = t10;
    $[38] = t11;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== inputError) {
    t16 = inputError && <span id="email-error" className={clsx("text-xsmall", "text-red-dark")}><AlertIcon /> Please enter a valid email</span>;
    $[42] = inputError;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t15 || $[45] !== t16) {
    t17 = <div className={t9}>{t15}{t16}</div>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== isTravelNewsLetter) {
    t18 = clsx("bg-green-default", "text-white", "text-small", "font-medium", "rounded", "px-4", {
      "px-6": !isTravelNewsLetter
    }, "box-border", "w-full", {
      "lg:w-[207px]": isTravelNewsLetter
    }, "h-[44px]", "flex", "items-center", "justify-center", "hover:bg-green-dark", "hover:cursor-pointer");
    $[47] = isTravelNewsLetter;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== buttonText || $[50] !== submitting) {
    t19 = submitting ? <Spinner /> : buttonText;
    $[49] = buttonText;
    $[50] = submitting;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== submitting || $[53] !== t18 || $[54] !== t19) {
    t20 = <button type="submit" disabled={submitting} className={t18}>{t19}</button>;
    $[52] = submitting;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== t17 || $[57] !== t20 || $[58] !== t8) {
    t21 = <div className={t8}>{t17}{t20}</div>;
    $[56] = t17;
    $[57] = t20;
    $[58] = t8;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== handleSubmit || $[61] !== t21) {
    t22 = <form className={t6} onSubmit={handleSubmit}>{t7}{t21}</form>;
    $[60] = handleSubmit;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  return t22;
};